
import { z } from "zod";
import { UpdateClaimRequest } from "@/api/generated/client";

/** Email field that trims whitespace and treats empty/whitespace-only strings as null before validating */
const optionalEmail = z.preprocess(
    (val) => {
        if (typeof val === "string") {
            const trimmed = val.trim();
            return trimmed === "" ? null : trimmed;
        }
        return val === "" ? null : val;
    },
    z.string().email("Invalid email address").nullable(),
).optional();

// 1. Define Validations (The Schema)
// We map this to UpdateClaimRequest from the API
export const updateClaimSchema = z.object({
    claimNumber: z.string().min(1, "Claim number cannot be empty").optional(),
    policyNumber: z.string().min(1, "Policy number cannot be empty").optional().nullable(),
    insuredName: z.string().min(1, "Insured Name is required").optional(),
    insuredPhone: z.string().optional().nullable(),
    insuredPhone2: z.string().optional().nullable(),
    insuredEmail: optionalEmail,
    insuredEmail2: optionalEmail,

    // Date and Enum validations
    dateOfLoss: z.string().optional().nullable(), // API expects string (YYYY-MM-DD) or ISO
    propertyAddress: z.string().optional().nullable(),
    mailingAddress: z.string().max(500, "Mailing address must be less than 500 characters").optional().nullable(),
    propertySquareFootage: z.number().min(0, "Square footage must be positive").optional().nullable(),
    description: z.string().optional().nullable(),

    // Insurance info
    insuranceCarrier: z.string().max(255, "Insurance carrier must be less than 255 characters").optional().nullable(),
    contractor: z.string().max(255, "Contractor must be less than 255 characters").optional().nullable(),
    internalFileNumber: z.string().max(100, "Internal file number must be less than 100 characters").optional().nullable(),

    // Status template reference (optional in updates - if omitted, keeps existing value)
    claimStatusTemplateId: z.number().optional(),
    lossType: z.string().max(100, "Loss type must be less than 100 characters").optional().nullable(),
});

// 2. Infer Types
export type UpdateClaimForm = z.infer<typeof updateClaimSchema>;

// 3. DRIFT PROTECTION
// Ensure our form stays compatible with Backend Types
// We check that our UpdateClaimForm's fields match UpdateClaimRequest
export type _BackendCheck = Pick<UpdateClaimForm, "insuredName" | "insuredPhone" | "insuredEmail" | "propertySquareFootage"> extends Pick<UpdateClaimRequest, "insuredName" | "insuredPhone" | "insuredEmail" | "propertySquareFootage"> ? true : false;
// Note: We can expand this check to all shared fields
export type _BackendCheckAll = UpdateClaimForm extends UpdateClaimRequest ? true : false;
